import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider } from "react-hook-form"

import { SurchargeForm } from "../../forms/SurchargeForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { PricingGridRow } from "@/screens/Pricing/components/tables/types"
import { useSurchargeForm } from "@/screens/Pricing/hooks/useSurchargeForm"
import type { toSurchargeRowUpdate } from "@/screens/Pricing/utils/surcharge"

interface SurchargeFormDialogProps {
  row: PricingGridRow
  onClose: () => void
  onSubmit: (update: ReturnType<typeof toSurchargeRowUpdate>) => void
}

export const SurchargeFormDialog: FC<SurchargeFormDialogProps> = ({ row, onClose, onSubmit }) => {
  const {
    methods,
    fields,
    handleAppend,
    handleRemove,
    handleSubmit,
    handleCancel,
    canSubmit,
    canAppend,
    canRemove,
    isCancelConfirmationOpen,
    setIsCancelConfirmationOpen,
  } = useSurchargeForm({ row, onSubmit, onClose })

  return (
    <Dialog open onClose={handleCancel} maxWidth='sm' fullWidth data-testid='pricing.surcharge-form-dialog'>
      {isCancelConfirmationOpen ? (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogHeader title='Edit Surcharge' onClose={handleCancel} />
          <DialogContent>
            <FormProvider {...methods}>
              <SurchargeForm
                fields={fields}
                canAppend={canAppend}
                canRemove={canRemove}
                onAppend={handleAppend}
                onRemove={handleRemove}
              />
            </FormProvider>
          </DialogContent>
          <DialogActions>
            <Box className='flex w-full justify-between p-4'>
              <Button
                appearance='outlined'
                onClick={handleCancel}
                data-testid='pricing.surcharge-form-dialog.cancel-button'
              >
                Cancel
              </Button>
              <Button
                appearance='contained'
                onClick={handleSubmit}
                disabled={!canSubmit}
                data-testid='pricing.surcharge-form-dialog.submit-button'
              >
                Save
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
